import { initializeApp } from 'firebase/app'
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  verifyBeforeUpdateEmail,
  type User,
} from 'firebase/auth'
import {
  initializeFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  increment,
  updateDoc,
  runTransaction,
} from 'firebase/firestore'
import { shopData } from '../components/pages/site/shopData'
import { bookData } from '../components/pages/site/bookData'

const firebaseConfig = {
  apiKey: 'AIzaSyAjpYThNrNWDdcKp6N8mn1m4B52Ty__NnE',
  authDomain: 'shop-16968.firebaseapp.com',
  projectId: 'shop-16968',
  storageBucket: 'shop-16968.firebasestorage.app',
  messagingSenderId: '1057983305014',
  appId: '1:1057983305014:web:4e63c75557b7c47bcbcf97',
  measurementId: 'G-4M5D03LZMT',
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const storage = getStorage(app)
const functions = getFunctions(app)
// Initialize Firestore with long-polling forced ON instead of the default
// WebChannel streaming transport. The default streaming Watch transport is
// fragile inside sandboxed iframe / CDN-bundled environments and, under the
// rapid subscribe/unsubscribe churn of React 18 StrictMode double-mounting
// (orders + cart + sales + review listeners), corrupts the stream's internal
// target version state — surfacing as:
//   "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state (ID: b815)"
//   with nested (ID: ca9) {"ve":-1}.
// Forcing long-polling uses a request/response transport that does not hold a
// long-lived stream, which sidesteps that corrupted-watch-stream assertion.
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
})

// ─── Types ───────────────────────────────────────────────

export interface Address {
  id: string
  recipientName: string
  line1: string
  line2?: string
  city: string
  state: string
  postalCode: string
  country: string
  isDefault?: boolean
  deliveryInstructions?: string
}

export interface CustomerProfile {
  email: string
  displayName: string
  createdAt: string
  marketingOptIn?: boolean
  addresses?: Address[]
  role?: 'admin' | 'manager' | 'support' | 'customer'
}

export interface OrderLineItem {
  productId: string
  name: string
  quantity: number
  unitPrice: number // in cents
  type: 'book' | 'supplement'
}

export interface ShippingAddress {
  recipientName?: string
  company?: string
  line1: string
  line2?: string
  city: string
  state: string
  postalCode: string
  country: string
  deliveryInstructions?: string
}

export interface CheckoutShippingAddressSnapshot {
  sessionId: string
  userUid: string
  email: string
  address: ShippingAddress
  createdAt: string
}

/**
 * Converts the customer's default saved address into the immutable order
 * address shape. Used to repair legacy physical orders that were created
 * before checkout address snapshots were persisted.
 */
export function getPreferredShippingAddress(
  addresses: Address[] | undefined,
): ShippingAddress | undefined {
  const address = addresses?.find((item) => item.isDefault) ?? addresses?.[0]
  if (!address?.line1?.trim()) return undefined

  return {
    recipientName: address.recipientName,
    line1: address.line1,
    ...(address.line2?.trim() ? { line2: address.line2 } : {}),
    city: address.city,
    state: address.state,
    postalCode: address.postalCode,
    country: address.country,
    ...(address.deliveryInstructions?.trim()
      ? { deliveryInstructions: address.deliveryInstructions }
      : {}),
  }
}

export interface Order {
  id: string
  userUid: string
  email: string
  stripeSessionId: string
  paymentIntentId?: string | null
  lineItems: OrderLineItem[]

  subtotal: number // in cents
  shipping: number // in cents
  tax: number // in cents
  discount: number // in cents
  total: number // in cents

  currency?: string

  status: 'paid' | 'refunded' | 'pending'
  fulfillmentStatus: 'pending' | 'shipped' | 'delivered' | 'digital_delivered'

  shippingAddress?: ShippingAddress
  trackingNumber?: string
  trackingCarrier?: string
  downloadUrl?: string // for digital books

  createdAt: string
  updatedAt: string
}

// ─── Auth Functions ───────────────────────────────────────────────

/**
 * Create a new customer account with Firebase Auth + Firestore profile.
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
  marketingOptIn = false,
): Promise<{ uid: string; email: string }> {
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  const user = credential.user

  await updateProfile(user, { displayName })

  const profile: CustomerProfile = {
    email: email.trim().toLowerCase(),
    displayName,
    createdAt: new Date().toISOString(),
    marketingOptIn,
  }

  await setDoc(doc(db, 'customers', user.uid), profile)

  // Send verification email (non-blocking — don't fail signup if this errors)
  try {
    await sendEmailVerification(user)
  } catch {
    // ignore — user can request resend later
  }

  return { uid: user.uid, email: profile.email }
}

/**
 * Sign in an existing customer.
 */
export async function signIn(
  email: string,
  password: string,
): Promise<{ uid: string; email: string }> {
  const credential = await signInWithEmailAndPassword(auth, email, password)
  const user = credential.user
  return { uid: user.uid, email: user.email ?? email }
}

/**
 * Sign out the current user.
 */
export async function signOutUser(): Promise<void> {
  await signOut(auth)
}

/**
 * Send a password reset email.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

/**
 * Resend verification email to the currently signed-in user.
 */
export async function resendVerificationEmail(): Promise<void> {
  const user = auth.currentUser
  if (user && !user.emailVerified) {
    await sendEmailVerification(user)
  }
}

/**
 * Update the signed-in user's display name (Firebase Auth + Firestore profile).
 * No reauthentication required.
 */
export async function updateDisplayName(name: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('Not signed in.')
  const trimmed = name.trim()
  if (!trimmed) throw new Error('Please enter your name.')
  await updateProfile(user, { displayName: trimmed })
  await updateCustomerProfile(user.uid, { displayName: trimmed })
}

/**
 * Change the signed-in user's email address. Firebase requires a recent
 * login, so the current password is used to reauthenticate first. A
 * verification email is sent to the NEW address; the change takes effect
 * once the user clicks that link.
 */
export async function updateUserEmail(
  newEmail: string,
  currentPassword: string,
): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('Not signed in.')
  const clean = newEmail.trim().toLowerCase()
  if (!clean.includes('@')) throw new Error('Please enter a valid email.')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await verifyBeforeUpdateEmail(user, clean)
  // Reflect the pending email in Firestore so the profile stays in sync.
  await updateCustomerProfile(user.uid, { email: clean })
}

/**
 * Change the signed-in user's password. Reauthenticates with the current
 * password first (Firebase requires a recent login for this operation).
 */
export async function changeUserPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('Not signed in.')
  if (newPassword.length < 6)
    throw new Error('Password must be at least 6 characters.')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await updatePassword(user, newPassword)
}

/**
 * Read a customer profile from Firestore.
 */
export async function getCustomerProfile(
  uid: string,
): Promise<CustomerProfile | null> {
  if (!uid.trim()) return null
  const snap = await getDoc(doc(db, 'customers', uid))
  return snap.exists() ? (snap.data() as CustomerProfile) : null
}

/**
 * Resolves a profile by its order email. This protects legacy payment records
 * that were written without a usable Firebase UID.
 */
export async function getCustomerProfileByEmail(
  email: string,
): Promise<CustomerProfile | null> {
  const normalizedEmail = email.trim().toLowerCase()
  if (!normalizedEmail) return null
  const profiles = await getDocs(
    query(
      collection(db, 'customers'),
      where('email', '==', normalizedEmail),
    ),
  )
  return profiles.empty ? null : (profiles.docs[0].data() as CustomerProfile)
}

/**
 * Update a customer profile in Firestore (merges with existing data).
 */
export async function updateCustomerProfile(
  uid: string,
  data: Partial<CustomerProfile>,
): Promise<void> {
  await setDoc(doc(db, 'customers', uid), data, { merge: true })
}

/**
 * Subscribe to a user's auth state changes.
 * Returns the unsubscribe function.
 */
export function subscribeToAuth(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback)
}

// ─── Order Functions ───────────────────────────────────────────────

/**
 * Subscribe to a customer's orders, sorted newest first.
 * Returns the unsubscribe function.
 */
export function subscribeToOrders(
  uid: string,
  callback: (orders: Order[]) => void,
): () => void {
  const q = query(
    collection(db, 'orders'),
    where('userUid', '==', uid),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Order[]
      callback(orders)
    },
    (err) => {
      // If the composite index doesn't exist yet, log so dev sees it
      console.error('[Firebase] subscribeToOrders error:', err)
      callback([])
    },
  )
}

export function subscribeToAllOrders(
  callback: (orders: Order[]) => void,
): () => void {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Order[]
      callback(orders)
    },
    (err) => {
      console.error('[Firebase] subscribeToAllOrders error:', err)
      callback([])
    },
  )
}

/**
 * One-time fetch of a customer's orders (alternative to subscription).
 */
export async function getOrder(orderId: string): Promise<Order | null> {
  const snap = await getDoc(doc(db, 'orders', orderId))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Order) : null
}

/**
 * Stores the exact address selected for a Stripe checkout under that checkout
 * session. It remains available to fulfillment even when the payment-side
 * order writer omits the address or a customer later edits their address book.
 */
export async function saveCheckoutShippingAddressSnapshot(
  sessionId: string,
  address: ShippingAddress | undefined,
): Promise<void> {
  const user = auth.currentUser
  if (!user || !address?.line1?.trim() || !sessionId.trim()) return

  const snapshot: CheckoutShippingAddressSnapshot = {
    sessionId,
    userUid: user.uid,
    email: (user.email ?? '').trim().toLowerCase(),
    address,
    createdAt: new Date().toISOString(),
  }
  await setDoc(doc(db, 'checkout_address_snapshots', sessionId), snapshot)
}

export async function getCheckoutShippingAddressSnapshot(
  sessionId: string,
): Promise<ShippingAddress | undefined> {
  if (!sessionId.trim()) return undefined
  const snapshot = await getDoc(doc(db, 'checkout_address_snapshots', sessionId))
  if (!snapshot.exists()) return undefined
  const data = snapshot.data() as Partial<CheckoutShippingAddressSnapshot>
  return normalizeShippingAddress(data.address)
}

/**
 * Resolves an order's delivery address from the immutable order snapshot
 * first, then its exact checkout-session snapshot, followed by profile data
 * for legacy orders. The email fallback supports payment records without UID.
 */
export async function resolveOrderShippingAddress(
  order: Order,
): Promise<ShippingAddress | undefined> {
  const rawOrder = order as unknown as Record<string, unknown>
  const orderAddress =
    normalizeShippingAddress(order.shippingAddress) ??
    normalizeShippingAddress(rawOrder.shipping_address) ??
    normalizeShippingAddress(rawOrder.deliveryAddress) ??
    normalizeShippingAddress(rawOrder.delivery_address) ??
    normalizeShippingAddress(rawOrder.address)
  if (orderAddress) return orderAddress

  try {
    const checkoutAddress = await getCheckoutShippingAddressSnapshot(
      order.stripeSessionId,
    )
    if (checkoutAddress) return checkoutAddress
  } catch (error) {
    console.error('[Orders] Could not read checkout address snapshot:', error)
  }

  try {
    const profileByUid = order.userUid
      ? await getCustomerProfile(order.userUid)
      : null
    const profile = profileByUid ?? (await getCustomerProfileByEmail(order.email))
    return getPreferredShippingAddress(profile?.addresses)
  } catch (error) {
    console.error('[Orders] Could not resolve legacy profile address:', error)
    return undefined
  }
}

function normalizeShippingAddress(value: unknown): ShippingAddress | undefined {
  if (!value || typeof value !== 'object') return undefined
  const data = value as Record<string, unknown>
  const line1 = stringValue(data.line1) ?? stringValue(data.address1)
  const city = stringValue(data.city)
  const state = stringValue(data.state) ?? stringValue(data.province)
  const postalCode =
    stringValue(data.postalCode) ??
    stringValue(data.postal_code) ??
    stringValue(data.zip)
  const country = stringValue(data.country)
  if (!line1 || !city || !state || !postalCode || !country) return undefined

  const recipientName =
    stringValue(data.recipientName) ??
    stringValue(data.recipient_name) ??
    stringValue(data.name)
  const company = stringValue(data.company)
  const line2 = stringValue(data.line2) ?? stringValue(data.address2)
  const deliveryInstructions = stringValue(data.deliveryInstructions)
  return {
    ...(recipientName ? { recipientName } : {}),
    ...(company ? { company } : {}),
    line1,
    ...(line2 ? { line2 } : {}),
    city,
    state,
    postalCode,
    country,
    ...(deliveryInstructions ? { deliveryInstructions } : {}),
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/**
 * Mark a physical order as fulfilled. This is intentionally limited to the
 * fulfillment fields so customer/order/payment data cannot be changed here.
 */
export async function markOrderAsShipped(orderId: string): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    fulfillmentStatus: 'shipped',
    updatedAt: new Date().toISOString(),
  })
}

/**
 * Backfills the selected checkout address onto its matching paid order when a
 * payment-side order writer did not persist the checkout request payload. The
 * order remains authoritative: an existing address is never overwritten, and
 * the signed-in customer can only update their own paid physical order.
 */
export async function attachOrderShippingAddress(
  orderId: string,
  address: ShippingAddress,
): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('You must be signed in to save a shipping address.')

  await runTransaction(db, async (transaction) => {
    const orderRef = doc(db, 'orders', orderId)
    const orderSnapshot = await transaction.get(orderRef)
    if (!orderSnapshot.exists()) throw new Error('Order not found.')

    const order = orderSnapshot.data() as Order
    if (order.userUid !== user.uid) {
      throw new Error('This order does not belong to the signed-in customer.')
    }
    if (order.status !== 'paid') {
      throw new Error('The shipping address can only be saved after payment.')
    }
    if (!order.lineItems.some((item) => item.type === 'supplement')) return
    if (order.shippingAddress?.line1) return

    transaction.update(orderRef, {
      shippingAddress: address,
      updatedAt: new Date().toISOString(),
    })
  })
}

// ─── Product image storage ─────────────────────────────────────────────────

export async function uploadProductImage(
  file: File,
  productName: string,
): Promise<string> {
  const user = auth.currentUser

  if (!user) {
    throw new Error('You must be signed in to upload product images.')
  }

  const allowedTypes = [
    'image/png',
    'image/jpeg',
    'image/webp',
  ]

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Product images must be PNG, JPG, or WebP.')
  }

  const maxSize = 10 * 1024 * 1024

  if (file.size > maxSize) {
    throw new Error('Product images must be 10 MB or smaller.')
  }

  const safeName = productName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'product'

  const extension =
    file.name.split('.').pop()?.toLowerCase() || 'png'

  const fileName = `${safeName}-${Date.now()}.${extension}`

  const imageRef = ref(
    storage,
    `product-images/${fileName}`,
  )

  await uploadBytes(imageRef, file, {
    contentType: file.type,
  })

  return getDownloadURL(imageRef)
}

// ─── Product catalog and inventory ─────────────────────────────────────────

export type FulfillmentType = 'physical' | 'digital'

export const CATALOG_CATEGORIES = [
  'Protein',
  'Strength',
  'Endurance',
  'Health',
  'Stress',
  'Sleep',
  'Books',
] as const

export type CatalogCategory = (typeof CATALOG_CATEGORIES)[number]

export interface CatalogProduct {
  id: string
  name: string
  image: string
  price: number // cents

  /** Stripe lookup key used to identify the current Stripe price. */
  lookupKey?: string

  fulfillmentType: FulfillmentType

  /** Physical products use this value. Digital products are not decremented. */
  stock: number

  /** Product shipping information */
  shipping?: {
    weight: number
    dimensions: {
      length: number
      width: number
      height: number
    }
  }

  /** A product can appear in more than one public storefront category. */
  categories: CatalogCategory[]

  /** Hidden products stay in the admin catalog but never appear publicly. */
  visible: boolean

  createdAt: string
  updatedAt: string
}

export interface CatalogProductInput {
  id?: string
  name: string
  image: string
  price: number
  lookupKey?: string
  fulfillmentType: FulfillmentType
  stock: number

  shipping?: {
    weight: number
    dimensions: {
      length: number
      width: number
      height: number
    }
  }

  categories: CatalogCategory[]
  visible: boolean
}

// Existing catalog entries have no recorded stock count yet, so seed them at
// zero rather than inventing inventory. Admins set the opening count once.
const DEFAULT_PHYSICAL_STOCK = 0
const LOCAL_CATALOG_KEY = 'sn_local_catalog_products'

export interface CatalogSaveResult {
  product: CatalogProduct
  persistence: 'firestore' | 'local'
}

function createCatalogId(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return normalized || `product-${Date.now()}`
}

function getStaticWebsiteCatalog(): CatalogProduct[] {
  const now = new Date().toISOString()
  const supplements = shopData.map((product) => ({
    id: product.id,
    name: product.name,
    image: product.image,
    price: product.price,
    fulfillmentType: 'physical' as const,
    stock: DEFAULT_PHYSICAL_STOCK,
    categories: [product.category],
    visible: true,
    createdAt: now,
    updatedAt: now,
  }))
  const guide = bookData.find((book) => book.id === 'glp1-nutrition-guide')
  if (!guide) return supplements
  return [
    ...supplements,
    {
      id: guide.id,
      name: guide.title,
      image: guide.coverImage ?? '',
      price: guide.priceCents ?? 0,
      fulfillmentType: 'digital' as const,
      stock: 0,
      categories: ['Books'],
      visible: true,
      createdAt: now,
      updatedAt: now,
    },
  ]
}

function readLocalCatalogProducts(): CatalogProduct[] {
  if (typeof window === 'undefined') return []
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(LOCAL_CATALOG_KEY) ?? '[]',
    )
    if (!Array.isArray(stored)) return []
    return stored.filter(isCatalogProduct)
  } catch {
    return []
  }
}

function writeLocalCatalogProducts(products: CatalogProduct[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCAL_CATALOG_KEY, JSON.stringify(products))
  } catch (error) {
    console.error('[Products] Could not store local catalog changes:', error)
    throw new Error('Local browser storage is unavailable.')
  }
}

function mergeCatalogProducts(
  base: CatalogProduct[],
  overrides: CatalogProduct[],
): CatalogProduct[] {
  const products = new Map(base.map((product) => [product.id, product]))
  overrides.forEach((product) => products.set(product.id, product))
  return Array.from(products.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  )
}

export function getWebsiteCatalogFallback(): CatalogProduct[] {
  return mergeCatalogProducts(getStaticWebsiteCatalog(), readLocalCatalogProducts())
}

function defaultCatalogCategories(productId: string): CatalogCategory[] {
  const supplement = shopData.find((product) => product.id === productId)
  if (supplement) return [supplement.category]
  return productId === 'glp1-nutrition-guide' ? ['Books'] : []
}

function isCatalogCategory(value: unknown): value is CatalogCategory {
  return (
    typeof value === 'string' &&
    (CATALOG_CATEGORIES as readonly string[]).includes(value)
  )
}

function isCatalogProduct(value: unknown): value is CatalogProduct {
  if (!value || typeof value !== 'object') return false
  const product = value as Partial<CatalogProduct>
  return (
    typeof product.id === 'string' &&
    typeof product.name === 'string' &&
    typeof product.image === 'string' &&
    typeof product.price === 'number' &&
    (product.fulfillmentType === 'physical' || product.fulfillmentType === 'digital') &&
    typeof product.stock === 'number' &&
    Array.isArray(product.categories) &&
    product.categories.every(isCatalogCategory) &&
    typeof product.visible === 'boolean' &&
    typeof product.createdAt === 'string' &&
    typeof product.updatedAt === 'string'
  )
}

/** Seed and safely migrate the website catalog without overwriting admin edits. */
export async function seedWebsiteProducts(): Promise<void> {
  const existing = await getDocs(collection(db, 'products'))
  const existingIds = new Set(existing.docs.map((snapshot) => snapshot.id))
  const seeds = getStaticWebsiteCatalog()
  const missing = seeds.filter((product) => !existingIds.has(product.id))
  const fieldMigrations = existing.docs.flatMap((snapshot) => {
    const data = snapshot.data() as Partial<CatalogProduct>
    const patch: Partial<CatalogProduct> = {}
    if (!Array.isArray(data.categories)) {
      patch.categories = defaultCatalogCategories(snapshot.id)
    }
    if (typeof data.visible !== 'boolean') {
      patch.visible = true
    }
    return Object.keys(patch).length > 0
      ? [setDoc(snapshot.ref, patch, { merge: true })]
      : []
  })

  await Promise.all([
    ...missing.map((product) => setDoc(doc(db, 'products', product.id), product)),
    ...fieldMigrations,
  ])
}

export function subscribeToCatalogProducts(
  callback: (products: CatalogProduct[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(db, 'products'),
    (snapshot) => {
      const products = snapshot.docs
        .map((document) => {
          const data = document.data() as Partial<CatalogProduct>
          const categories = Array.isArray(data.categories)
            ? data.categories.filter(isCatalogCategory)
            : defaultCatalogCategories(document.id)
          return {
            ...data,
            id: document.id,
            categories,
            visible: data.visible !== false,
          } as CatalogProduct
        })
      callback(products)
    },
    (error) => {
      console.error('[Firebase] subscribeToCatalogProducts error:', error)
      onError?.(error)
    },
  )
}

export function subscribeToVisibleCatalogProducts(
  callback: (products: CatalogProduct[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, 'products'),
    where('visible', '==', true),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const products = snapshot.docs.map((document) => {
        const data = document.data() as Partial<CatalogProduct>
        const categories = Array.isArray(data.categories)
          ? data.categories.filter(isCatalogCategory)
          : defaultCatalogCategories(document.id)

        return {
          ...data,
          id: document.id,
          categories,
          visible: data.visible !== false,
        } as CatalogProduct
      })

console.log(
  'Visible products from Firestore:',
  products.map((p) => ({
    id: p.id,
    name: p.name,
    visible: p.visible,
  })),
)

      callback(products)
    },
    (error) => {
      console.error('[Firebase] subscribeToVisibleCatalogProducts error:', error)
      onError?.(error)
    },
  )
}

function saveLocalCatalogProduct(product: CatalogProduct): void {
  const products = readLocalCatalogProducts().filter(
    (existing) => existing.id !== product.id,
  )
  writeLocalCatalogProducts([...products, product])
}

function removeLocalCatalogProduct(productId: string): void {
  const products = readLocalCatalogProducts().filter(
    (product) => product.id !== productId,
  )
  writeLocalCatalogProducts(products)
}
interface SyncCatalogProductPriceRequest {
  lookupKey: string
  amount: number
  productName: string
}

interface SyncCatalogProductPriceResponse {
  changed: boolean
  priceId: string
  amount: number
  lookupKey: string
}

async function syncCatalogProductPrice(
  lookupKey: string,
  amount: number,
  productName: string,
): Promise<SyncCatalogProductPriceResponse> {
  const callable = httpsCallable<
    SyncCatalogProductPriceRequest,
    SyncCatalogProductPriceResponse
  >(functions, 'syncCatalogProductPrice')

  const result = await callable({
    lookupKey,
    amount,
    productName,
  })

  return result.data
}
export interface CreatePaymentIntentV2Request {
  lineItems: Array<{
    price?: string
    lookupKey?: string
    quantity: number
  }>
  shippingAddress: ShippingAddress
  email: string
}

export interface CreatePaymentIntentV2Response {
  clientSecret: string
  paymentIntentId: string
}

export async function createPaymentIntentV2(
  input: CreatePaymentIntentV2Request,
): Promise<CreatePaymentIntentV2Response> {
  const callable = httpsCallable<
    CreatePaymentIntentV2Request,
    CreatePaymentIntentV2Response
  >(functions, 'createPaymentIntentV2')

  const result = await callable(input)

  return result.data
}

export interface CreateHostedCheckoutRequest {
  lineItems: Array<{
    priceId?: string
    lookupKey?: string
    quantity: number
  }>
  hasPhysicalItems: boolean
}

export interface CreateHostedCheckoutResponse {
  sessionId: string
  clientSecret: string
}

export interface GuestOrderConfirmationResponse {
  orderId: string
  email: string
  status: string
  fulfillmentStatus: string

  lineItems: Array<{
    name: string
    quantity: number
    unitPrice: number
  }>

  subtotal: number
  shipping: number
  tax: number
  discount: number
  total: number

  shippingAddress: {
    recipientName: string
    line1: string
    line2: string
    city: string
    state: string
    postalCode: string
    country: string
  } | null
}

export async function createHostedCheckout(
  input: CreateHostedCheckoutRequest,
): Promise<CreateHostedCheckoutResponse> {
  const callable = httpsCallable<
    CreateHostedCheckoutRequest,
    CreateHostedCheckoutResponse
  >(functions, 'createHostedCheckout')

  const result = await callable(input)
  return result.data
}

export async function getGuestOrderConfirmation(
  sessionId: string,
): Promise<GuestOrderConfirmationResponse> {
  const callable = httpsCallable<
    { sessionId: string },
    GuestOrderConfirmationResponse
  >(functions, 'getGuestOrderConfirmation')

  const result = await callable({ sessionId })
  return result.data
}

export interface UpdateHostedCheckoutShippingRequest {
  checkoutSessionId: string
  shippingDetails: {
    name?: string
    address: {
      line1?: string
      line2?: string | null
      city?: string
      state?: string
      postal_code?: string
      country?: string
    }
  }
}

export interface UpdateHostedCheckoutShippingResponse {
  shippingAmount: number
  freeShipping: boolean
}

export async function updateHostedCheckoutShipping(
  input: UpdateHostedCheckoutShippingRequest,
): Promise<UpdateHostedCheckoutShippingResponse> {
  const callable = httpsCallable<
    UpdateHostedCheckoutShippingRequest,
    UpdateHostedCheckoutShippingResponse
  >(functions, 'updateHostedCheckoutShipping')

  const result = await callable(input)
  return result.data
}
export async function saveCatalogProduct(
  input: CatalogProductInput,
): Promise<CatalogSaveResult> {
  const id = input.id ?? createCatalogId(input.name)
  const now = new Date().toISOString()

  const localVersion = readLocalCatalogProducts().find(
    (product) => product.id === id,
  )

  const categories = Array.from(
    new Set(input.categories.filter(isCatalogCategory)),
  )

const product: CatalogProduct = {
  id,
  name: input.name,
  image: input.image,
  price: input.price,
  ...(input.lookupKey?.trim()
    ? { lookupKey: input.lookupKey.trim() }
    : {}),
  fulfillmentType: input.fulfillmentType,
  stock: input.stock,

  shipping: input.shipping ?? {
    weight: 0,
    dimensions: {
      length: 0,
      width: 0,
      height: 0,
    },
  },

  categories,
  visible: input.visible,
  createdAt: localVersion?.createdAt ?? now,
  updatedAt: now,
}

  const writeData: Omit<CatalogProduct, 'createdAt'> & {
    createdAt?: string
  } = {
    ...product,
    ...(input.id ? {} : { createdAt: product.createdAt }),
  }

  try {
    if (product.lookupKey) {
      await syncCatalogProductPrice(
        product.lookupKey,
        product.price,
        product.name,
      )
    }

    await setDoc(doc(db, 'products', id), writeData, { merge: true })

    removeLocalCatalogProduct(id)

    return {
      product,
      persistence: 'firestore',
    }
  } catch (error) {
console.log("FULL STRIPE SYNC ERROR", error);
console.log("ERROR DETAILS", (error as any)?.details);
console.log("ERROR CODE", (error as any)?.code);
console.log("ERROR MESSAGE", (error as any)?.message);    console.warn(
      '[Products] Firestore/Stripe save unavailable; saving locally:',
      error,
    )

    saveLocalCatalogProduct(product)

    return {
      product,
      persistence: 'local',
    }
  }
}
export async function reconcileOrderInventory(order: Order): Promise<void> {
  if (order.status !== 'paid') return
  const physicalItems = order.lineItems.filter(
    (item) => item.type === 'supplement' && item.quantity > 0,
  )
  if (!physicalItems.length) return

  await runTransaction(db, async (transaction) => {
    const adjustmentRef = doc(db, 'inventory_adjustments', order.id)
    const priorAdjustment = await transaction.get(adjustmentRef)
    if (priorAdjustment.exists()) return

    const productReads = await Promise.all(
      physicalItems.map((item) =>
        transaction.get(doc(db, 'products', item.productId)),
      ),
    )
    const now = new Date().toISOString()
    const appliedItems: { productId: string; quantity: number }[] = []

    productReads.forEach((snapshot, index) => {
      if (!snapshot.exists()) return
      const product = snapshot.data() as CatalogProduct
      const item = physicalItems[index]
      if (product.fulfillmentType !== 'physical') return
      transaction.update(snapshot.ref, {
        stock: Math.max(0, Math.floor(product.stock ?? 0) - item.quantity),
        updatedAt: now,
      })
      appliedItems.push({ productId: item.productId, quantity: item.quantity })
    })

    transaction.set(adjustmentRef, {
      orderId: order.id,
      stripeSessionId: order.stripeSessionId,
      appliedItems,
      createdAt: now,
    })
  })
}

// ─── Sales Analytics (automatic best sellers) ─────────────────────────────
//
// A single aggregate document tallies how many units of each product have
// sold. The storefront reads this to automatically tag the top sellers — no
// hand-maintained "best seller" list required.
//
// Stored at: analytics/salesByProduct
//   { counts: { [productId]: number }, updatedAt: string }
//
// In production the authoritative writer is the Stripe webhook (server side),
// which increments these counts when a payment is confirmed. `recordProductSales`
// below is the client-side bridge so the system also accumulates sales in this
// app immediately after a successful checkout.
//
// IMPORTANT: Your Firestore rules must allow public read on this doc so the
// shop can rank sellers, and allow the increment writes you intend to use:
//
//   match /analytics/salesByProduct {
//     allow read: if true;
//     allow write: if request.auth != null;
//   }

export interface SalesByProduct {
  counts: Record<string, number>
  updatedAt: string
}

/**
 * Real-time subscription to the per-product sales tally used to rank best
 * sellers. Fires immediately with the current counts, then on every change.
 * Returns an empty map when the doc doesn't exist yet (brand-new site).
 */
export function subscribeToSalesByProduct(
  callback: (counts: Record<string, number>) => void,
): () => void {
  return onSnapshot(
    doc(db, 'analytics', 'salesByProduct'),
    (snap) => {
      if (!snap.exists()) {
        callback({})
        return
      }
      const data = snap.data() as SalesByProduct
      callback(
        data.counts && typeof data.counts === 'object' ? data.counts : {},
      )
    },
    (err) => {
      console.error('[Firebase] subscribeToSalesByProduct error:', err)
      callback({})
    },
  )
}

/**
 * Increment the sales tally for the given purchased items. Called after a
 * confirmed checkout so best-seller ranking reflects real demand. Each entry
 * bumps `counts.<productId>` by its quantity using an atomic Firestore
 * increment, so concurrent purchases never clobber each other.
 */
export async function recordProductSales(
  items: { productId: string; quantity: number }[],
): Promise<void> {
  if (!items.length) return
  const payload: Record<string, unknown> = {
    updatedAt: new Date().toISOString(),
  }
  for (const { productId, quantity } of items) {
    if (!productId || quantity <= 0) continue
    payload[`counts.${productId}`] = increment(quantity)
  }
  await setDoc(doc(db, 'analytics', 'salesByProduct'), payload, { merge: true })
}

// ─── Newsletter Functions ───────────────────────────────────────────────

/**
 * Add an email to the newsletter signup list.
 * Uses the lowercased email as the doc ID so re-submissions are idempotent.
 *
 * Stored at: newsletter_signups/{email}
 *
 * IMPORTANT: Your Firestore rules must allow public create on this
 * collection. Add a rule like:
 *
 *   match /newsletter_signups/{email} {
 *     allow create: if request.resource.data.keys().hasOnly(
 *       ['email', 'source', 'createdAt']
 *     );
 *   }
 */
export async function subscribeToNewsletter(
  email: string,
  source: string = 'unknown',
): Promise<void> {
  const cleanEmail = email.trim().toLowerCase()
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address.')
  }
  await setDoc(
    doc(db, 'newsletter_signups', cleanEmail),
    {
      email: cleanEmail,
      source,
      createdAt: new Date().toISOString(),
    },
    { merge: true },
  )
}

/**
 * Shape of a stored newsletter signup document.
 */
export interface NewsletterSignup {
  email: string
  source: string
  createdAt: string
}

/**
 * Real-time subscription to every newsletter signup, newest first.
 * Used by the admin area to view all emails collected across the site.
 *
 * IMPORTANT: Your Firestore rules must allow admin reads on this collection:
 *
 *   match /newsletter_signups/{email} {
 *     allow read: if request.auth != null; // (tighten to admins in prod)
 *   }
 */
export function subscribeToNewsletterSignups(
  callback: (signups: NewsletterSignup[]) => void,
): () => void {
  const q = query(
    collection(db, 'newsletter_signups'),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const signups = snap.docs.map((d) => d.data() as NewsletterSignup)
      callback(signups)
    },
    (err) => {
      console.error('[Firebase] subscribeToNewsletterSignups error:', err)
      callback([])
    },
  )
}

// ─── Cart Functions ───────────────────────────────────────────────

/**
 * Persisted cart shape stored at `carts/{uid}`. Items are intentionally
 * a minimal serializable list — the React layer rehydrates derived state.
 */
export interface PersistedCartItem {
  productId: string
  name: string
  unitPrice: number // in cents
  quantity: number
  type: 'book' | 'supplement'
  stripePriceId?: string
  lookupKey?: string
  accent?: string
}

export interface PersistedCart {
  items: PersistedCartItem[]
  updatedAt: string
}

/**
 * One-time fetch of a user's cart from Firestore.
 * Returns an empty array if no cart doc exists yet.
 */
export async function getUserCart(uid: string): Promise<PersistedCartItem[]> {
  const snap = await getDoc(doc(db, 'carts', uid))
  if (!snap.exists()) return []
  const data = snap.data() as PersistedCart
  return Array.isArray(data.items) ? data.items : []
}

/**
 * Write the user's cart to Firestore. Replaces any prior cart entirely.
 */
export async function setUserCart(
  uid: string,
  items: PersistedCartItem[],
): Promise<void> {
  await setDoc(doc(db, 'carts', uid), {
    items,
    updatedAt: new Date().toISOString(),
  })
}

/**
 * Real-time subscription to a user's cart.
 * Fires immediately with the current cart, then on every change.
 */
export function subscribeToUserCart(
  uid: string,
  callback: (items: PersistedCartItem[]) => void,
): () => void {
  return onSnapshot(
    doc(db, 'carts', uid),
    (snap) => {
      // Ignore snapshots that originate from our own un-acknowledged local
      // writes. These "echo" snapshots can arrive interleaved with rapid
      // cart edits and would otherwise stomp the freshly-added item back to
      // an older server state. We only act on server-confirmed snapshots.
      if (snap.metadata.hasPendingWrites) return
      if (!snap.exists()) {
        callback([])
        return
      }
      const data = snap.data() as PersistedCart
      callback(Array.isArray(data.items) ? data.items : [])
    },
    (err) => {
      console.error('[Firebase] subscribeToUserCart error:', err)
      callback([])
    },
  )
}

/**
 * Clear the user's cart in Firestore (used after a real Stripe checkout success).
 * Keep an explicit empty document instead of deleting it: standard customer cart
 * rules permit the same write used for cart updates, whereas deletes are often
 * intentionally disallowed.
 */
export async function clearUserCart(uid: string): Promise<void> {
  await setUserCart(uid, [])
}

// ─── Reviews (verified-purchase gated) ────────────────────────────────────
//
// Only customers who have actually bought a product can review it. The data
// model already proves purchase: an order in `orders` with the buyer's
// `userUid`, `status: 'paid'`, and the product in its `lineItems`.
//
// A review is stored at `reviews/{productId}_{uid}` — a deterministic id that
// enforces ONE review per product per customer (re-submitting edits theirs).
//
// IMPORTANT: Firestore security rules are the real gate (client checks are
// just UX). Recommended rules:
//
//   match /reviews/{reviewId} {
//     allow read: if true;
//     allow create, update: if request.auth != null
//       && request.resource.data.userUid == request.auth.uid
//       && request.resource.data.rating is number
//       && request.resource.data.rating >= 1
//       && request.resource.data.rating <= 5
//       && exists(/databases/$(database)/documents/orders/$(request.resource.data.orderId))
//       && get(/databases/$(database)/documents/orders/$(request.resource.data.orderId)).data.userUid == request.auth.uid
//       && get(/databases/$(database)/documents/orders/$(request.resource.data.orderId)).data.status == 'paid';
//   }
//   match /analytics/reviewStats {
//     allow read: if true;
//     allow write: if request.auth != null;
//   }
//
// (The rule verifies ownership + paid status via the referenced order. The
// client additionally verifies the product is actually in that order's line
// items before allowing the form, which rules can't easily check on arrays.)

export interface Review {
  id: string
  productId: string
  userUid: string
  displayName: string
  rating: number // 1..5
  title: string
  body: string
  orderId: string
  verifiedPurchase: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Returns the id of a PAID order belonging to `uid` that contains `productId`,
 * or null if the customer hasn't purchased it. This is the proof-of-purchase
 * used to gate reviewing.
 */
export async function findPurchaseOrderId(
  uid: string,
  productId: string,
): Promise<string | null> {
  const q = query(
    collection(db, 'orders'),
    where('userUid', '==', uid),
    where('status', '==', 'paid'),
  )
  const snap = await getDocs(q)
  for (const d of snap.docs) {
    const order = d.data() as Order
    if (order.lineItems?.some((li) => li.productId === productId)) {
      return d.id
    }
  }
  return null
}

/**
 * Real-time list of reviews for a product, newest first.
 */
export function subscribeToProductReviews(
  productId: string,
  callback: (reviews: Review[]) => void,
): () => void {
  const q = query(
    collection(db, 'reviews'),
    where('productId', '==', productId),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const reviews = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Review[]
      callback(reviews)
    },
    (err) => {
      console.error('[Firebase] subscribeToProductReviews error:', err)
      callback([])
    },
  )
}

/**
 * Fetch the current user's existing review for a product (if any) so the form
 * can prefill / show "you've already reviewed this".
 */
export async function getUserReviewForProduct(
  productId: string,
  uid: string,
): Promise<Review | null> {
  const snap = await getDoc(doc(db, 'reviews', `${productId}_${uid}`))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Review) : null
}

/**
 * Create or update the signed-in customer's review for a product. The caller
 * must pass a valid `orderId` proving purchase (from `findPurchaseOrderId`).
 * Also keeps the `analytics/reviewStats` aggregate in sync so product cards
 * can show an average rating + count without loading every review.
 */
export async function submitReview(input: {
  productId: string
  userUid: string
  displayName: string
  rating: number
  title: string
  body: string
  orderId: string
}): Promise<void> {
  const { productId, userUid, rating } = input
  if (rating < 1 || rating > 5) throw new Error('Rating must be 1–5 stars.')

  const reviewId = `${productId}_${userUid}`
  const ref = doc(db, 'reviews', reviewId)

  // Read the prior review (if any) to compute aggregate deltas.
  const prior = await getDoc(ref)
  const priorRating = prior.exists() ? (prior.data() as Review).rating : null
  const priorCreatedAt = prior.exists()
    ? (prior.data() as Review).createdAt
    : null

  const now = new Date().toISOString()
  const review: Omit<Review, 'id'> = {
    productId,
    userUid,
    displayName: input.displayName,
    rating,
    title: input.title,
    body: input.body,
    orderId: input.orderId,
    verifiedPurchase: true,
    createdAt: priorCreatedAt ?? now,
    updatedAt: now,
  }
  await setDoc(ref, review)

  // Update the rolling aggregate: sum of ratings + count of reviews.
  const sumDelta = priorRating === null ? rating : rating - priorRating
  const countDelta = priorRating === null ? 1 : 0
  await setDoc(
    doc(db, 'analytics', 'reviewStats'),
    {
      stats: {
        [productId]: {
          sum: increment(sumDelta),
          count: increment(countDelta),
        },
      },
      updatedAt: now,
    },
    { merge: true },
  )
}

export interface ReviewStat {
  sum: number
  count: number
}

/**
 * Real-time subscription to per-product review aggregates (sum + count of
 * ratings). Returns an empty map when no reviews exist yet.
 */
export function subscribeToReviewStats(
  callback: (stats: Record<string, ReviewStat>) => void,
): () => void {
  return onSnapshot(
    doc(db, 'analytics', 'reviewStats'),
    (snap) => {
      if (!snap.exists()) {
        callback({})
        return
      }
      const data = snap.data() as { stats?: Record<string, ReviewStat> }
      callback(data.stats && typeof data.stats === 'object' ? data.stats : {})
    },
    (err) => {
      console.error('[Firebase] subscribeToReviewStats error:', err)
      callback({})
    },
  )
}

// ─── Order Number Formatting ──────────────────────────────────────────

/**
 * Deterministically formats an alphanumeric order ID into a numeric string
 * matching the pattern XXX-XXXXXXX-XXXXXXX (e.g., 112-2096626-0392227).
 */
export function formatOrderNumber(id: string): string {
  let hash1 = 5381
  let hash2 = 52711
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i)
    hash1 = ((hash1 << 5) + hash1) ^ char
    hash2 = ((hash2 << 5) + hash2) ^ char
  }
  const part1 = Math.abs(hash1).toString().padStart(10, '1')
  const part2 = Math.abs(hash2).toString().padStart(10, '2')
  const combined = (part1 + part2).slice(0, 17)
  return `${combined.slice(0, 3)}-${combined.slice(3, 10)}-${combined.slice(10, 17)}`
}

export type { User }

export async function claimGuestOrders() {
  const callable = httpsCallable(functions, "claimGuestOrders");

  const result = await callable();

  return result.data as {
    success: boolean;
    claimed: number;
  };
}